import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {DialogService} from './dialog.service';
import {AbstractService} from './abstract.service';
import {first, tap} from 'rxjs/operators';
import {GCS_OAUTH_TOKEN, MS_GRAPH_TOKEN, OAUTH2_TOKEN, SCOPES, TOKEN_CREATION_TIME} from '../constants/local-storage-constants';
import {Callback} from '../models/callback.model';
import {CustomSnackBarService} from './custom-snackbar.service';
import firebase from "firebase";
import {AngularFireAuth} from "@angular/fire/auth";
import {environment} from '../../environments/environment';
import OAuthProvider = firebase.auth.OAuthProvider;
import GoogleAuthProvider = firebase.auth.GoogleAuthProvider;

@Injectable({
  providedIn: 'root'
})
export class AuthService extends AbstractService {
  private readonly AUTH_TYPE: string = 'authType';

  authChecked = new BehaviorSubject<boolean>(false);
  profile: any;
  profileSubject: Subject<any> = new Subject<any>();
  authErrorMessage$ = new Subject<string>();

  constructor(private readonly http: HttpClient,
              private readonly router: Router,
              private readonly afAuth: AngularFireAuth,
              private readonly dialogService: DialogService,
              private readonly snackBarService: CustomSnackBarService) {
    super();
  }

  logout(message?: string): void {
    this.setToken(null);
    this.setProfile(null, null);
    this.setScopes(null);
    localStorage.removeItem(MS_GRAPH_TOKEN);
    this.dialogService.closeAll();
    if (message) {
      this.snackBarService.openWarningSnackbar(message);
    }
    this.router.navigate(['login']);
  }

  setToken(token: string): void {
    token ? localStorage.setItem(OAUTH2_TOKEN, token) : localStorage.removeItem(OAUTH2_TOKEN);
  }

  getToken(): string {
    return localStorage.getItem(OAUTH2_TOKEN);
  }

  setScopes(scopes: string): void {
    scopes ? localStorage.setItem(SCOPES, scopes) : localStorage.removeItem(SCOPES);
  }

  getScopes(): string[] {
    if (!localStorage.getItem(SCOPES)) {
      return null;
    }
    return localStorage.getItem(SCOPES).split(' ');
  }

  setProfile(profile, callback: Callback): void {
    this.profile = profile;
    this.profileSubject.next({profile, callback});
  }

  async signInWithGoogle(route?: Callback): Promise<void> {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    try {
      const credential = await this.afAuth.signInWithPopup(provider);
      const user = credential.user;
      const firebaseIdToken = await user.getIdToken();

      this.setToken(firebaseIdToken);
      this.setScopes('email profile openid');
      localStorage.setItem(TOKEN_CREATION_TIME, String(Math.floor(Date.now() / 1000)));
      localStorage.setItem(this.AUTH_TYPE, 'google');

      this.setProfile(this.normalizeGoogleProfile(user), route);
    } catch (error) {
      console.error('Google login failed:', error);
      this.authErrorMessage$.next('Google login failed');
    }
  }

  // Used by settings-management to request additional Google OAuth scopes (e.g. Drive label creation).
  // Stores the broader-scoped OAuth access token so subsequent API calls can use it.
  async signInWithGoogleWithAdditionalScopes(authDetails: any, scopes: string): Promise<void> {
    this.setToken(authDetails.accessToken);
    this.setScopes(scopes);
    localStorage.setItem(TOKEN_CREATION_TIME, String(Math.floor(Date.now() / 1000)));
    localStorage.setItem(this.AUTH_TYPE, 'google');
  }

  // Requests the GCS storage scope via the OAuth 2.0 implicit-grant flow:
  // a full-page redirect to accounts.google.com, then back with the access
  // token in the URL hash. Avoids both the Firebase popup and the GIS Token
  // Client popup, which were unreliable across browsers/COOP configurations.
  //
  // The actual redirect-handling happens in src/main.ts before Angular boots
  // (necessary under useHash:true routing). This method only kicks off the
  // outbound navigation.
  requestGoogleStorageScope(scopes: string): Promise<void> {
    // Save state so the post-redirect bootstrap handler in main.ts can
    // restore the original page and remember which scope we requested.
    localStorage.setItem('pending_gcs_return_url', window.location.href);
    localStorage.setItem('pending_gcs_scopes', scopes);

    const params = new URLSearchParams({
      client_id: environment.clientId,
      redirect_uri: window.location.origin + '/',
      response_type: 'token',
      scope: scopes,
      include_granted_scopes: 'true',
      prompt: 'consent',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

    // Page is navigating away; this promise will never resolve.
    return new Promise<void>(() => undefined);
  }

  getGcsToken(): string {
    return localStorage.getItem(GCS_OAUTH_TOKEN);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  async restoreSession(): Promise<void> {
    const token = this.getToken();
    if (!token) {
      this.authChecked.next(true);
      return;
    }

    // Capture the URL from the hash before any async work gives
    // Angular's router a chance to clear it via guard rejections.
    const hashPath = window.location.hash.replace(/^#/, '');
    const restoreCallback: Callback = hashPath && hashPath !== '/' && hashPath !== '/login'
      ? {url: hashPath, params: null}
      : null;

    try {
      if (localStorage.getItem(this.AUTH_TYPE) === 'google') {
        // afAuth.currentUser resolves before Firebase restores the persisted session on page
        // refresh, returning null. authState waits for Firebase to finish initializing first.
        const currentUser = await this.afAuth.authState.pipe(first()).toPromise();
        if (!currentUser) {
          throw new Error('No Firebase user found');
        }
        const freshToken = await currentUser.getIdToken();
        this.setToken(freshToken);
        localStorage.setItem(TOKEN_CREATION_TIME, String(Math.floor(Date.now() / 1000)));
        this.setProfile(this.normalizeGoogleProfile(currentUser), restoreCallback);
      } else {
        await this.retrieveMicrosoftUserInfo(localStorage.getItem(MS_GRAPH_TOKEN)).pipe(
          tap((msProfile) => this.setProfile(this.normalizeMicrosoftProfile(msProfile), restoreCallback)),
          first()
        ).toPromise();
      }

    } catch (e) {
      console.warn('Failed to restore session:', e.message);
      this.logout();
    } finally {
      this.authChecked.next(true); // Mark as completed
    }
  }


  async signInWithMicrosoft(route?: Callback) {
    const provider = new OAuthProvider('microsoft.com');

    // Scopes should be putted correctly
    provider.addScope('email');
    provider.addScope('profile');

    try {
      const credential = await this.afAuth.signInWithPopup(provider);

      const user = credential.user;
      const firebaseIdToken = await user.getIdToken();
      const accessToken = (credential.credential as any)?.accessToken;

      // Store Firebase ID token for backend authentication
      this.setToken(firebaseIdToken);
      // Store MS Graph access token separately — used only for Graph API calls
      if (accessToken) {
        localStorage.setItem(MS_GRAPH_TOKEN, accessToken);
      }
      this.setScopes('email profile');
      localStorage.setItem(TOKEN_CREATION_TIME, String(Math.floor(Date.now() / 1000)));

      // Microsoft doesn't have a simple "userinfo" endpoint like Google so we call Graph API
      this.retrieveMicrosoftUserInfo(accessToken).pipe(
        tap(msProfile => this.setProfile(this.normalizeMicrosoftProfile(msProfile), route)),
        first()
      ).subscribe({
        error: async (e: HttpErrorResponse) => {
          this.setToken(null);
          this.authErrorMessage$.next(e.error);
          await this.router.navigate(['login']);
        }
      });

      localStorage.setItem(this.AUTH_TYPE, 'microsoft')

    } catch (error) {
      console.error('Microsoft login failed:', error);
      this.authErrorMessage$.next('Microsoft login failed');
    }
  }

  retrieveMicrosoftUserInfo(accessToken: string): Observable<any> {
    return this.http.get<any>('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
  }

  private normalizeGoogleProfile(user: any): any {
    return {
      name: user.displayName,
      email: user.email,
      given_name: user.displayName?.split(' ')[0] || '',
      family_name: user.displayName?.split(' ').slice(1).join(' ') || '',
      picture: user.photoURL,
      email_verified: user.emailVerified,
      locale: null,
    };
  }

  private normalizeMicrosoftProfile(msProfile: any): any {
    return {
      name: msProfile.displayName,
      email: msProfile.mail || msProfile.userPrincipalName,
      given_name: msProfile.givenName,
      family_name: msProfile.surname,
      locale: msProfile.preferredLanguage,
      picture: null,
      email_verified: true,
    };
  }
}
